import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { PaginationData, PaginationRange } from 'tiime-components';

import { HttpHelper } from '@core/helpers/http.helper';

/**
 * API Common Service.
 */
@Injectable({ providedIn: 'root' })
export abstract class ApiCommonService<T, Json = any> {
  protected readonly httpClient: HttpClient = inject(HttpClient);

  abstract resourceUrl: string;
  abstract fromJson(json: Json): T;
  abstract toJson(model: T): unknown;

  /**
   * Get entity by identifier.
   *
   * @param id the entity identifier.
   * @returns the funded entity.
   */
  protected getById(
    id: number,
    url: string = `${this.resourceUrl}/${id}`,
    options?: { params?: HttpParams; headers?: HttpHeaders }
  ): Observable<T> {
    return this.httpClient.get(url, options).pipe(map((json: Json) => this.fromJson(json)));
  }

  /**
   * Get all entities.
   *
   * @param options the http call options. It can include http params and http headers.
   * @param url the endpoint url if it is different than default resource url.
   * @returns the entities.
   */
  protected getAll(
    options?: { params?: HttpParams; headers?: HttpHeaders },
    url: string = this.resourceUrl
  ): Observable<T[]> {
    return this.httpClient
      .get(url, options || {})
      .pipe(map((jsonArray: Json[]) => jsonArray.map((json: Json) => this.fromJson(json))));
  }

  /**
   * Get paginated entities.
   *
   * @param range the paginated range.
   * @param params the http parameters.
   * @param url  the endpoint url if it is different than default resource url.
   * @returns the paginated entities.
   */
  protected getPaginated(
    range: PaginationRange,
    params: HttpParams,
    url: string = this.resourceUrl
  ): Observable<PaginationData<T>> {
    const headers = HttpHelper.setRangeHeader(new HttpHeaders(), range);
    const options = { params, headers };

    return this.httpClient.get(url, { ...options, observe: 'response' }).pipe(
      filter((response: HttpResponse<Json[]>) => response.status !== 204),
      HttpHelper.mapToPaginationData(range, json => this.fromJson(json))
    );
  }

  /**
   * Search entities.
   *
   * @param params the search parameters
   * @param url the endpoint url if it is different than default resource url.
   * @returns the found entities.
   */
  protected search(
    options?: { params?: HttpParams; headers?: HttpHeaders },
    url: string = this.resourceUrl
  ): Observable<T[]> {
    return this.httpClient
      .get<Json[]>(url, options)
      .pipe(map((jsonArray: Json[]) => jsonArray.map(json => this.fromJson(json))));
  }

  /**
   * Create an entity.
   *
   * @param data the entity data.
   * @param url the endpoint url if it is different than default resource url.
   * @param options the http call options. It can include http params and http headers.
   * @returns the created entity.
   */
  protected create(
    data: T,
    url: string = this.resourceUrl,
    options?: { params?: HttpParams; headers?: HttpHeaders }
  ): Observable<T> {
    const body = data ? this.toJson(data) : null;
    return this.httpClient.post<Json>(url, body, options).pipe(map((json: Json) => this.fromJson(json)));
  }

  /**
   * Create multiple entities.
   *
   * @param data the entities data.
   * @param url the endpoint url if it is different than default resource url.
   * @returns the created entity.
   */
  protected createMultiple(data: T[], url: string = this.resourceUrl): Observable<T[]> {
    const body = data.map((model: T) => this.toJson(model));
    return this.httpClient
      .post<Json[]>(url, body)
      .pipe(map((jsonArray: Json[]) => jsonArray.map(json => this.fromJson(json))));
  }

  /**
   * Update an entity.
   *
   * @param id the entity identifier to update.
   * @param data the entity data.
   * @param url the endpoint url if it is different than default resource url.
   * @param options Options to pass to patch method
   * @returns the updated entity.
   */
  protected update(
    id: number,
    data: T,
    url: string = `${this.resourceUrl}/${id}`,
    options?: Parameters<HttpClient['patch']>[2]
  ): Observable<T> {
    return this.httpClient.patch<Json>(url, this.toJson(data), options).pipe(map((json: Json) => this.fromJson(json)));
  }

  /**
   * Update partially an entity.
   *
   * @param id the entity identifier to update.
   * @param data the partial entity data.
   * @returns the updated entity.
   */
  protected updatePartial(
    id: number,
    data: Partial<Json>,
    options?: { params?: HttpParams; headers?: HttpHeaders }
  ): Observable<T> {
    const url = `${this.resourceUrl}/${id}`;
    return this.httpClient.patch<Json>(url, data, options).pipe(map((json: Json) => this.fromJson(json)));
  }

  /**
   * Update multiple entities.
   *
   * @param method the method to use
   * @param data the entities data.
   * @param url the endpoint url if it is different than default resource url.
   * @returns the updated entities.
   */
  protected updateMultiple(
    data: T[],
    url: string = this.resourceUrl,
    method: 'POST' | 'PUT' | 'PATCH' = 'PUT'
  ): Observable<T[]> {
    const body = data.map((model: T) => this.toJson(model));
    return this.httpClient
      .request<Json[]>(method, url, { body })
      .pipe(map((jsonArray: Json[]) => jsonArray.map((json: Json) => this.fromJson(json))));
  }

  /**
   * Delete an entity.
   *
   * @param id the entity identifier to delete.
   * @param url the endpoint url if it is different than default resource url.
   * @param options Options to pass to patch method
   */
  protected delete(
    id: number,
    url: string = `${this.resourceUrl}/${id}`,
    options?: Parameters<HttpClient['delete']>[1]
  ): Observable<unknown> {
    return this.httpClient.delete(url, options);
  }

  /**
   * Get Blob Http Response.
   *
   * @param url the endpoint url
   */
  protected blobHttpResponse(url: string): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response'
    });
  }
}
